import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Grid, Divider,
  IconButton, Avatar, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import BusinessIcon from "@material-ui/icons/Business";
import WarningIcon from "@material-ui/icons/Warning";
import { WhatsApp } from "@material-ui/icons";
import { toast } from "react-toastify";
import { format } from "date-fns";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  dialogPaper: { borderRadius: 12 },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: 8,
    marginTop: 12,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  contactCard: {
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 12,
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  contactInfo: { flex: 1, minWidth: 0 },
  contactName: { fontWeight: 700, fontSize: "0.95rem" },
  contactSub: { fontSize: "0.78rem", color: "#888" },
  fieldRow: { marginBottom: 10 },
  chip: { borderRadius: 8 },
  statusChip: {
    borderRadius: 20,
    fontWeight: 600,
    fontSize: "0.72rem",
  },
  divider: { margin: "14px 0" },
  helperText: {
    fontSize: "0.72rem",
    color: "#999",
    marginTop: 2,
  },
}));

const CRM_STATUS = ["Não iniciado", "Contato", "Em andamento", "Proposta", "Follow up", "Sucesso", "Perdido"];

const STATUS_COLORS = {
  "Não iniciado": "#9e9e9e",
  "Contato": "#2196f3",
  "Em andamento": "#ff9800",
  "Proposta": "#9c27b0",
  "Follow up": "#00bcd4",
  "Sucesso": "#4caf50",
  "Perdido": "#f44336",
};

const today = format(new Date(), "yyyy-MM-dd");

const CrmCreateCardModal = ({ open, onClose, tags = [], initialLaneId = null, onCreated }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId: userWhatsappId } = user;

  // Busca de contato
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Campos do negócio
  const [crmStatus, setCrmStatus] = useState("Não iniciado");
  const [valorNegocio, setValorNegocio] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [laneId, setLaneId] = useState(initialLaneId || "");

  // Criação do ticket
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");

  const [saving, setSaving] = useState(false);

  // Erros de validação
  const [errors, setErrors] = useState({});

  // Duplicidade de número: já existe um card ativo para o mesmo telefone
  // (considerando variações de DDI/9º dígito), possivelmente num Contact
  // diferente do selecionado.
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Definir lane inicial quando o modal abre
  useEffect(() => {
    if (open) {
      setLaneId(initialLaneId != null ? String(initialLaneId) : "");
      setSelectedContact(null);
      setContactSearch("");
      setCrmStatus("Não iniciado");
      setValorNegocio("");
      setDataInicio("");
      setDataFim("");
      setSaving(false);
      setErrors({});
      setDuplicateInfo(null);
    }
  }, [open, initialLaneId]);

  // Ao selecionar um contato, verifica se o número já tem um card ativo no
  // CRM (mesmo que salvo num outro Contact, com/sem DDI ou 9º dígito).
  useEffect(() => {
    if (!selectedContact) {
      setDuplicateInfo(null);
      return;
    }
    let cancelled = false;
    setCheckingDuplicate(true);
    setDuplicateInfo(null);
    api
      .get(`/tickets/check-duplicate-crm/${selectedContact.id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setDuplicateInfo(data.duplicate ? data : null);
      })
      .catch(err => {
        if (!cancelled) toastError(err);
      })
      .finally(() => {
        if (!cancelled) setCheckingDuplicate(false);
      });
    return () => { cancelled = true; };
  }, [selectedContact]);

  // Buscar conexões e filas ao abrir
  useEffect(() => {
    if (!open) return;
    const fetchResources = async () => {
      try {
        const [whatsRes] = await Promise.all([
          api.get("/whatsapp", { params: { companyId, session: 0 } }),
        ]);
        setWhatsapps(whatsRes.data || []);
        if (userWhatsappId) setSelectedWhatsapp(userWhatsappId);
      } catch (err) {
        toastError(err);
      }
    };
    fetchResources();

    setQueues(user.queues || []);
    if (user.queues?.length === 1) setSelectedQueue(user.queues[0].id);
  }, [open]);

  // Debounce busca de contatos. Cada busca tem uma "geração" própria — se uma
  // resposta chegar depois de uma busca mais recente já ter começado, ela é
  // descartada. Sem isso, uma resposta atrasada de uma busca antiga podia
  // sobrescrever os resultados da busca atual (a lista "piscava"/desaparecia
  // enquanto o usuário ainda digitava).
  const searchGenerationRef = useRef(0);
  useEffect(() => {
    if (!open) return;
    setLoadingContacts(true);
    const currentGeneration = ++searchGenerationRef.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/list", {
          params: {
            name: contactSearch || undefined,
            excludeBusy: "false",
          },
        });
        if (currentGeneration === searchGenerationRef.current) {
          setContactOptions(data || []);
        }
      } catch (err) {
        if (currentGeneration === searchGenerationRef.current) {
          toastError(err);
        }
      } finally {
        if (currentGeneration === searchGenerationRef.current) {
          setLoadingContacts(false);
        }
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [open, contactSearch]);

  const validate = () => {
    const newErrors = {};

    if (!selectedContact) newErrors.contact = "Selecione um contato";
    if (duplicateInfo) newErrors.contact = "Já existe um card ativo para este número";
    if (!selectedQueue) newErrors.queue = "Selecione um setor";

    if (dataInicio && dataInicio < today) {
      newErrors.dataInicio = "A data de início não pode ser no passado";
    }
    if (dataFim && dataFim < today) {
      newErrors.dataFim = "A data limite não pode ser no passado";
    }
    if (dataInicio && dataFim && dataFim < dataInicio) {
      newErrors.dataFim = "A data limite não pode ser anterior à data de início";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDataInicioChange = (e) => {
    const val = e.target.value;
    setDataInicio(val);
    const newErrors = { ...errors };
    if (!val || val >= today) delete newErrors.dataInicio;
    else newErrors.dataInicio = "A data de início não pode ser no passado";
    if (dataFim && val && dataFim < val) newErrors.dataFim = "A data limite não pode ser anterior à data de início";
    else if (dataFim && dataFim >= today && (!val || dataFim >= val)) delete newErrors.dataFim;
    setErrors(newErrors);
  };

  const handleDataFimChange = (e) => {
    const val = e.target.value;
    setDataFim(val);
    const newErrors = { ...errors };
    if (!val) { delete newErrors.dataFim; setErrors(newErrors); return; }
    if (val < today) newErrors.dataFim = "A data limite não pode ser no passado";
    else if (dataInicio && val < dataInicio) newErrors.dataFim = "A data limite não pode ser anterior à data de início";
    else delete newErrors.dataFim;
    setErrors(newErrors);
  };

  const handleCreate = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId: selectedContact.id,
        queueId: selectedQueue || null,
        whatsappId: selectedWhatsapp || null,
        userId: user.id,
        status: "open",
      });

      if (laneId && laneId !== "lane0" && laneId !== "") {
        await api.put(`/ticket-tags/${ticket.id}/${parseInt(laneId, 10)}`);
      }

      const updatePayload = {
        crmStatus,
        dataInicio: dataInicio || null,
        dataFim: dataFim || null,
      };
      if (valorNegocio) {
        updatePayload.valorVenda = parseFloat(valorNegocio);
      }
      await api.put(`/tickets/${ticket.id}`, updatePayload);

      toast.success("Card CRM criado com sucesso!");
      onCreated && onCreated(ticket);
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warn("Já existe um ticket aberto para este contato. O card foi associado ao ticket existente.");
        onCreated && onCreated(null);
        onClose();
        return;
      }
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const connectedWhatsapps = whatsapps.filter(w => w.status === "CONNECTED");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Criar Card CRM</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Busca de contato ── */}
        <Typography className={classes.sectionTitle}>Contato *</Typography>

        <Autocomplete
          options={contactOptions}
          loading={loadingContacts}
          getOptionLabel={opt => opt.name || ""}
          // A busca já é feita pelo backend (nome/número) a cada digitação —
          // não re-filtrar no client, ou contatos que só batem por número
          // (não pelo `name` usado em getOptionLabel) somem da lista mesmo
          // tendo vindo corretamente da API.
          filterOptions={opts => opts}
          value={selectedContact}
          onChange={(e, newVal) => {
            setSelectedContact(newVal);
            if (newVal) setErrors(prev => ({ ...prev, contact: undefined }));
          }}
          onInputChange={(e, val) => setContactSearch(val)}
          noOptionsText="Nenhum contato encontrado"
          loadingText="Buscando..."
          renderInput={params => (
            <TextField
              {...params}
              label="Buscar contato pelo nome ou número"
              variant="outlined"
              size="small"
              fullWidth
              error={!!errors.contact}
              helperText={errors.contact}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingContacts ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={opt => (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar src={opt.urlPicture} style={{ width: 32, height: 32, fontSize: 14 }}>
                {opt.name?.[0] || <PersonIcon />}
              </Avatar>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{opt.name}</div>
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  {opt.number}{opt.razaoSocial ? ` · ${opt.razaoSocial}` : ""}
                </div>
              </div>
            </div>
          )}
        />

        {selectedContact && (
          <div className={classes.contactCard} style={{ marginTop: 10 }}>
            <Avatar src={selectedContact.urlPicture} style={{ width: 40, height: 40 }}>
              {selectedContact.name?.[0] || <PersonIcon />}
            </Avatar>
            <div className={classes.contactInfo}>
              <Typography className={classes.contactName}>{selectedContact.name}</Typography>
              {selectedContact.number && (
                <Typography className={classes.contactSub}>
                  <WhatsApp style={{ fontSize: 13, verticalAlign: "middle", color: "#25d366" }} /> {selectedContact.number}
                </Typography>
              )}
              {selectedContact.razaoSocial && (
                <Typography className={classes.contactSub}>
                  <BusinessIcon style={{ fontSize: 13, verticalAlign: "middle" }} /> {selectedContact.razaoSocial}
                </Typography>
              )}
              {selectedContact.email && (
                <Typography className={classes.contactSub}>{selectedContact.email}</Typography>
              )}
            </div>
          </div>
        )}

        {checkingDuplicate && (
          <Typography className={classes.helperText} style={{ marginTop: -4, marginBottom: 10 }}>
            <CircularProgress size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Verificando duplicidade de número...
          </Typography>
        )}

        {duplicateInfo && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            background: "#fff3e0", border: "1px solid #ffb74d",
            borderRadius: 8, padding: "10px 12px", marginBottom: 10,
          }}>
            <WarningIcon style={{ color: "#e65100", fontSize: 20, marginTop: 1 }} />
            <Typography style={{ fontSize: "0.82rem", color: "#5d4037" }}>
              Este número já possui um card ativo no CRM
              {duplicateInfo.contact?.name ? ` em nome de "${duplicateInfo.contact.name}"` : ""}
              {duplicateInfo.contact?.number ? ` (${duplicateInfo.contact.number})` : ""}.
              Não é possível criar um card duplicado para o mesmo número.
            </Typography>
          </div>
        )}

        <Divider className={classes.divider} />

        {/* ── Coluna CRM ── */}
        <Typography className={classes.sectionTitle}>Coluna CRM</Typography>
        <FormControl variant="outlined" size="small" fullWidth className={classes.fieldRow}>
          <InputLabel>Coluna</InputLabel>
          <Select value={laneId} onChange={e => setLaneId(e.target.value)} label="Coluna">
            <MenuItem value="lane0"><em>Sem coluna (padrão)</em></MenuItem>
            {tags.map(tag => (
              <MenuItem key={tag.id} value={tag.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: "50%",
                    background: tag.color || "#757575", display: "inline-block"
                  }} />
                  {tag.name}
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider className={classes.divider} />

        {/* ── Dados do negócio ── */}
        <Typography className={classes.sectionTitle}>Dados do Negócio</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Status CRM</InputLabel>
              <Select value={crmStatus} onChange={e => setCrmStatus(e.target.value)} label="Status CRM">
                {CRM_STATUS.map(s => (
                  <MenuItem key={s} value={s}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: "50%",
                        background: STATUS_COLORS[s] || "#9e9e9e", display: "inline-block"
                      }} />
                      {s}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Valor do Negócio (R$)"
              type="number"
              value={valorNegocio}
              onChange={e => setValorNegocio(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Data de Início"
              type="date"
              value={dataInicio}
              onChange={handleDataInicioChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
              variant="outlined"
              size="small"
              fullWidth
              error={!!errors.dataInicio}
              helperText={errors.dataInicio}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Data Limite"
              type="date"
              value={dataFim}
              onChange={handleDataFimChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dataInicio || today }}
              variant="outlined"
              size="small"
              fullWidth
              error={!!errors.dataFim}
              helperText={errors.dataFim}
            />
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        {/* ── Configuração do ticket ── */}
        <Typography className={classes.sectionTitle}>Iniciar Conversa</Typography>
        <Typography className={classes.helperText} style={{ marginBottom: 10 }}>
          Ao criar o card, um ticket será aberto com este contato no setor selecionado.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              required
              error={!!errors.queue}
            >
              <InputLabel>Setor *</InputLabel>
              <Select
                value={selectedQueue}
                onChange={e => {
                  setSelectedQueue(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, queue: undefined }));
                }}
                label="Setor *"
              >
                {queues.map(q => (
                  <MenuItem key={q.id} value={q.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: "50%",
                        background: q.color || "#757575", display: "inline-block"
                      }} />
                      {q.name}
                    </div>
                  </MenuItem>
                ))}
              </Select>
              {errors.queue && (
                <Typography style={{ color: "#f44336", fontSize: "0.72rem", marginTop: 4, marginLeft: 14 }}>
                  {errors.queue}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {connectedWhatsapps.length > 1 && (
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Conexão WhatsApp</InputLabel>
                <Select
                  value={selectedWhatsapp}
                  onChange={e => setSelectedWhatsapp(e.target.value)}
                  label="Conexão WhatsApp"
                >
                  {connectedWhatsapps.map(w => (
                    <MenuItem key={w.id} value={w.id}>
                      <WhatsApp style={{ fontSize: 14, color: "#25d366", marginRight: 6, verticalAlign: "middle" }} />
                      {w.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={saving || checkingDuplicate || !!duplicateInfo}
          style={{ borderRadius: 8 }}
        >
          {saving ? <CircularProgress size={18} style={{ marginRight: 8 }} /> : null}
          Criar Card
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrmCreateCardModal;
